/**
 * Accessible custom listbox for pb-common-product-dropdown.
 * Syncs with hidden native <select> for buybox pricing / ATC.
 */
(function () {
  const openDropdowns = new Set();

  function getThumbBgUrl(wrapper) {
    return wrapper.dataset.pbThumbBg || '';
  }

  function initDropdown(wrapper) {
    if (!(wrapper instanceof HTMLElement)) return;
    if (wrapper.dataset.pbDropdownInit === 'true') return;

    const select = wrapper.querySelector('.pb-common-product-dropdown__native');
    const trigger = wrapper.querySelector('.pb-common-product-dropdown__trigger');
    const panel = wrapper.querySelector('[data-pb-common-product-dropdown-panel]');
    const listbox = wrapper.querySelector('.pb-common-product-dropdown__listbox');

    if (!(select instanceof HTMLSelectElement) || !(trigger instanceof HTMLButtonElement) || !panel || !listbox) {
      return;
    }

    const options = Array.from(select.options).filter((opt) => opt.value);
    if (!options.length) return;

    const thumbBgUrl = getThumbBgUrl(wrapper);

    let optionEls = Array.from(listbox.querySelectorAll('.pb-common-product-dropdown__option[role="option"]'));

    if (!optionEls.length) {
      optionEls = options.map((opt, index) => {
        const li = document.createElement('li');
        const optionId = `${listbox.id}-option-${index}`;
        const imageUrl = opt.dataset.image || '';

        li.id = optionId;
        li.className = 'pb-common-product-dropdown__option';
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', opt.selected ? 'true' : 'false');
        li.dataset.value = opt.value;

        const thumbWrap = document.createElement('span');
        thumbWrap.className = 'pb-common-product-dropdown__option-thumb-wrap';

        if (thumbBgUrl) {
          const thumbBg = document.createElement('img');
          thumbBg.className = 'pb-common-product-dropdown__option-thumb-bg';
          thumbBg.src = thumbBgUrl;
          thumbBg.alt = '';
          thumbBg.loading = 'lazy';
          thumbWrap.appendChild(thumbBg);
        }

        if (imageUrl) {
          const thumb = document.createElement('img');
          thumb.className = 'pb-common-product-dropdown__option-thumb';
          thumb.src = imageUrl;
          thumb.alt = '';
          thumb.width = 56;
          thumb.height = 40;
          thumb.loading = 'eager';
          thumbWrap.appendChild(thumb);
        }

        const label = document.createElement('span');
        label.className = 'pb-common-product-dropdown__option-label';
        label.textContent = opt.textContent.trim();

        li.appendChild(thumbWrap);
        li.appendChild(label);
        listbox.appendChild(li);

        return li;
      });
    }

    if (!optionEls.length) return;

    let activeIndex = Math.max(0, options.findIndex((opt) => opt.selected));
    let isOpen = false;

    const state = {
      wrapper,
      closeListbox: null,
      get isOpen() {
        return isOpen;
      },
      set isOpen(value) {
        isOpen = value;
        if (value) {
          openDropdowns.add(state);
        } else {
          openDropdowns.delete(state);
        }
      },
    };

    function getActiveOptionEl() {
      return optionEls[activeIndex] || null;
    }

    function setActiveIndex(index, scrollIntoView = false) {
      if (!optionEls.length) return;
      activeIndex = Math.max(0, Math.min(index, optionEls.length - 1));

      optionEls.forEach((el, i) => {
        el.classList.toggle('is-active', i === activeIndex);
      });

      const activeEl = getActiveOptionEl();
      if (activeEl) {
        listbox.setAttribute('aria-activedescendant', activeEl.id);
        if (scrollIntoView) {
          activeEl.scrollIntoView({ block: 'nearest' });
        }
      }
    }

    function paintTriggerFromOption(opt) {
      const label = trigger.querySelector('.pb-common-product-dropdown__label');
      const thumbWrap = trigger.querySelector('.pb-common-product-dropdown__thumb-wrap');
      const imageUrl = opt?.dataset.image || '';

      if (label) {
        label.textContent = opt?.textContent.trim() || '';
      }

      if (!thumbWrap) return;

      let thumb = thumbWrap.querySelector('.pb-common-product-dropdown__thumb');
      if (imageUrl) {
        if (!(thumb instanceof HTMLImageElement)) {
          thumb = document.createElement('img');
          thumb.className = 'pb-common-product-dropdown__thumb';
          thumb.width = 130;
          thumb.loading = 'lazy';
          thumb.decoding = 'async';
          thumb.alt = '';
          thumbWrap.appendChild(thumb);
        }
        thumb.src = imageUrl;
      } else if (thumb) {
        thumb.remove();
      }
    }

    function selectIndex(index, shouldClose = true) {
      if (index < 0 || index >= options.length) return;

      const opt = options[index];
      const prev = select.value;
      select.value = opt.value;

      optionEls.forEach((el, i) => {
        el.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });

      options.forEach((o, i) => {
        o.selected = i === index;
      });

      activeIndex = index;
      paintTriggerFromOption(opt);

      if (prev !== opt.value) {
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (shouldClose) {
        closeListbox(true);
      } else {
        setActiveIndex(index);
      }
    }

    function eagerLoadOptionImages() {
      optionEls.forEach((el) => {
        el.querySelectorAll('.pb-common-product-dropdown__option-thumb').forEach((img) => {
          if (img instanceof HTMLImageElement) {
            img.loading = 'eager';
          }
        });
      });
    }

    function openListbox() {
      if (state.isOpen || select.disabled || trigger.disabled) return;

      state.isOpen = true;
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      eagerLoadOptionImages();

      const selectedIndex = Math.max(0, options.findIndex((opt) => opt.value === select.value));
      setActiveIndex(selectedIndex, true);
      listbox.focus();
    }

    function closeListbox(returnFocus = false) {
      if (!state.isOpen) return;

      state.isOpen = false;
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      listbox.removeAttribute('aria-activedescendant');

      optionEls.forEach((el) => el.classList.remove('is-active'));

      if (returnFocus) {
        trigger.focus();
      }
    }

    function moveActive(delta) {
      if (!state.isOpen) {
        openListbox();
        return;
      }
      setActiveIndex(activeIndex + delta, true);
    }

    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (select.disabled || trigger.disabled) return;
      if (state.isOpen) {
        closeListbox(true);
      } else {
        openListbox();
      }
    });

    trigger.addEventListener('keydown', (event) => {
      if (select.disabled || trigger.disabled) return;

      switch (event.key) {
        case 'ArrowDown':
        case 'Down':
          event.preventDefault();
          if (!state.isOpen) {
            openListbox();
          } else {
            moveActive(1);
          }
          break;
        case 'ArrowUp':
        case 'Up':
          event.preventDefault();
          if (!state.isOpen) {
            openListbox();
          } else {
            moveActive(-1);
          }
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (!state.isOpen) {
            openListbox();
          } else {
            selectIndex(activeIndex);
          }
          break;
        case 'Escape':
          if (state.isOpen) {
            event.preventDefault();
            closeListbox(true);
          }
          break;
        case 'Home':
          if (state.isOpen) {
            event.preventDefault();
            setActiveIndex(0, true);
          }
          break;
        case 'End':
          if (state.isOpen) {
            event.preventDefault();
            setActiveIndex(optionEls.length - 1, true);
          }
          break;
        default:
          break;
      }
    });

    listbox.addEventListener('keydown', (event) => {
      switch (event.key) {
        case 'ArrowDown':
        case 'Down':
          event.preventDefault();
          moveActive(1);
          break;
        case 'ArrowUp':
        case 'Up':
          event.preventDefault();
          moveActive(-1);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          selectIndex(activeIndex);
          break;
        case 'Escape':
          event.preventDefault();
          closeListbox(true);
          break;
        case 'Home':
          event.preventDefault();
          setActiveIndex(0, true);
          break;
        case 'End':
          event.preventDefault();
          setActiveIndex(optionEls.length - 1, true);
          break;
        case 'Tab':
          closeListbox(false);
          break;
        default:
          break;
      }
    });

    optionEls.forEach((el, index) => {
      el.addEventListener('mousedown', (event) => {
        event.preventDefault();
      });

      el.addEventListener('click', () => {
        selectIndex(index);
      });
    });

    state.closeListbox = closeListbox;

    const initialIndex = Math.max(0, options.findIndex((opt) => opt.selected));
    paintTriggerFromOption(options[initialIndex]);
    setActiveIndex(initialIndex);

    wrapper.dataset.pbDropdownInit = 'true';
  }

  function initAll(root) {
    const scope = root instanceof HTMLElement || root instanceof Document ? root : document;
    scope.querySelectorAll('[data-pb-common-product-dropdown]').forEach(initDropdown);
  }

  function boot() {
    initAll(document);
  }

  if (!window.__pbCommonProductDropdownGlobalsBound) {
    window.__pbCommonProductDropdownGlobalsBound = true;

    document.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Node)) return;

      openDropdowns.forEach((state) => {
        if (!state.isOpen) return;
        if (state.wrapper.contains(target)) return;
        state.closeListbox?.(false);
      });
    });

    document.addEventListener('focusin', (event) => {
      const target = event.target;
      if (!(target instanceof Node)) return;

      openDropdowns.forEach((state) => {
        if (!state.isOpen) return;
        if (state.wrapper.contains(target)) return;
        state.closeListbox?.(false);
      });
    });

    document.addEventListener('shopify:section:load', (event) => {
      if (event.target instanceof HTMLElement) {
        initAll(event.target);
      }
    });
  }

  window.PbCommonProductDropdown = {
    init: initDropdown,
    initAll,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();